import { AnyAction, Middleware } from '@reduxjs/toolkit';

import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { isPWA } from 'app/middleware/visibility/utils/helpers';
import { AppState } from 'bootstrap.store';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
// eslint-disable-next-line no-restricted-imports
import { hasFeature } from 'components/shared/featureFlag';

import { jabraActions } from './jabra.actions';
import { VoiceConversationOffer } from './jabra.types';
import { jabraDeviceManager } from './utils/jabraDeviceManager';

export const jabraMiddleware: () => Middleware<unknown, AppState> =
  () => (store) => {
    let voiceConversationOffers: VoiceConversationOffer[] = [];
    return (next) => (action: AnyAction) => {
      const hasWebUsbJabraIntegration = hasFeature(
        store.getState(),
        'web-usb-jabra-integration',
      );

      if (hasWebUsbJabraIntegration && isPWA()) {
        const isTabOrWindowActive =
          store.getState().websocket.meta.isPrimaryConnection;

        if (isTabOrWindowActive) {
          jabraDeviceManager.reconnect();
        } else if (!isTabOrWindowActive) {
          jabraDeviceManager.disconnect();
        }

        switch (action.type) {
          /** Case when it's requested to setup a connected device */
          case jabraActions.jabraRequestDevice.toString(): {
            jabraDeviceManager
              .setupDevice()
              .catch((err) => console.warn('jabraRequestDevice', err));
            break;
          }
          /** Case when jabra devices are being requested */
          case jabraActions.jabraList.toString(): {
            /**
             * Set timeout to avoid race condition with Jabra saga
             * jabraAttachedGen and jabraDetachedGen generators
             */
            setTimeout(() => {
              store.dispatch(
                jabraActions.jabraListSucceeded({
                  list: jabraDeviceManager.devices.map((device) => ({
                    deviceID: device.id,
                    productID: device.productId,
                    deviceName: device.name,
                    serialNumber: device.serialNumber,
                    isLocked:
                      device.serialNumber ===
                      jabraDeviceManager.currentDevice?.serialNumber,
                  })),
                }),
              );
            }, 0);

            break;
          }
        }

        if (!jabraDeviceManager.hasLock) {
          return next(action);
        }

        switch (action.type) {
          /** Case when an incoming call is accepted from a device */
          case jabraActions.jabraCallAccepted.toString(): {
            const [voiceConversationOffer] = voiceConversationOffers;

            if (!voiceConversationOffer || !voiceConversationOffer.csid) break;

            store.dispatch(
              jabraActions.jabraButtonClickedAcceptedCallOfferConversation({
                did_accept: true,
                ...voiceConversationOffer,
              }),
            );
            break;
          }
          /** Case when an incoming call is rejected from a device */
          case jabraActions.jabraCallRejected.toString(): {
            const [voiceConversationOffer] = voiceConversationOffers;
            const hasVoiceConversationOffer = !!voiceConversationOffer;

            if (!hasVoiceConversationOffer) break;

            store.dispatch(
              jabraActions.jabraButtonClickedRejectedCallOfferConversation({
                did_accept: false,
                ...voiceConversationOffer,
              }),
            );
            break;
          }
          /** Case when testing jabra functionality for incoming call */
          case jabraActions.jabraRing.toString(): {
            if (!isTabOrWindowActive) break;

            jabraDeviceManager
              .signalIncomingCall()
              .then(() => store.dispatch(jabraActions.jabraRingSucceeded()))
              .catch((err) => console.warn('jabraRing', err));

            break;
          }
          /** Case when testing jabra functionality for hanging up call */
          case jabraActions.jabraUnring.toString(): {
            jabraDeviceManager
              .rejectIncomingCall()
              .then(() => {
                store.dispatch(jabraActions.jabraUnringSucceeded());
              })
              .catch((err) => console.warn('jabraUnring', err));

            break;
          }
          /** Case when there's an incoming call */
          case conversationActions.conversationWsTransferOfferReceived.toString():
          case conversationActions.conversationWsOfferReceived.toString(): {
            const {
              csid,
              reassignment,
              direction,
              transfer_type,
              channel,
              isEndUserConnected,
            } = action.payload.params ?? {};

            if (!['pstnphone', 'callback'].includes(channel)) {
              return voiceConversationOffers;
            }

            voiceConversationOffers = [
              ...voiceConversationOffers,
              {
                csid,
                reassignment,
                isInternal: direction === 'internal',
                transferType: transfer_type ?? null,
                channel: channel,
                isEndUserConnected,
              },
            ];

            if (!isTabOrWindowActive) break;

            jabraDeviceManager
              .signalIncomingCall()
              .catch((err) => console.warn('transfer/offer received', err));
            break;
          }
          /** Case when a conversation is accepted, rejected or cancelled */
          case conversationActions.conversationRespondeToOfferAccepted.toString():
          case conversationActions.conversationRespondeToTransferOfferAccepted.toString():
          case conversationActions.conversationRespondeToOfferRejected.toString():
          case conversationActions.conversationRespondeToTransferOfferRejected.toString():
          case conversationActions.conversationWsOfferCanceled.toString(): {
            const transferToAgentMetaCsid =
              store.getState().transferToAgent.metaConversation.conversation.id;
            const transferToNumberMetaCsid =
              store.getState().transferToNumber.metaConversation.conversation
                .id;
            const message = action.payload.message;
            const csid =
              message?.conference | message?.internal | message?.outbound;
            const activeTransferCsid =
              transferToAgentMetaCsid ?? transferToNumberMetaCsid;

            const isConversationStillInTransfer = activeTransferCsid === csid;

            if (isConversationStillInTransfer) break;

            const [voiceConversationOffer] = voiceConversationOffers;
            const hasVoiceConversationOffer = !!voiceConversationOffer;

            voiceConversationOffers = voiceConversationOffers.filter(
              (conversationOffer) =>
                conversationOffer.csid !==
                (action.payload?.params?.csid || action.payload.csid),
            );

            if (!hasVoiceConversationOffer) break;
            if (!isTabOrWindowActive) break;

            jabraDeviceManager
              .rejectIncomingCall()
              .catch((err) => console.warn('Jabra middleware', err));

            jabraDeviceManager
              .endCall({ emitHangup: !action.type.endsWith('ACCEPTED') })
              .catch((err) => console.warn('Jabra middleware', err));

            break;
          }
          case twilioActions.twilioEstablishedCall.toString(): {
            if (!isTabOrWindowActive) break;

            jabraDeviceManager
              .startCall()
              .catch((err) => console.warn('twilioEstablishedCall', err));
            break;
          }
          case twilioActions.twilioDisconnectedCall.toString(): {
            if (!isTabOrWindowActive) break;

            jabraDeviceManager
              .endCall({ emitHangup: false })
              .catch((err) => console.warn('twilioDisconnectedCall', err));
            break;
          }
        }
      }

      return next(action);
    };
  };
